import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useMemo, useRef, useState } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window';
import NoMatchOnSearch from 'flow/components/nav/empty-states/NoMatchOnSearch';
import { firstIndexToFocus, listItemKey, uniqueRowKeys } from 'flow/components/nav/list-keys/list-key-action';
import useListTabStop from 'flow/components/nav/list-keys/useListTabStop';
import Loading from 'flow/components/ui-components/Loading';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector } from 'flow/hooks/useRedux';
import { selectExtensionsAreLoading } from 'flow/selectors/contact-list-selectors';
import { isEmptyNullOrUndefined } from 'flow/utils';
import { saveDepartmentState } from 'flow/utils/browser-utils';
import { contactListRowHeight, contactListRowKey, rowIsFocusable } from './contact-list-rows';
import ContactListRow from './ContactListRow';
import useContactListRows from './useContactListRows';
import useContactListSelection from './useContactListSelection';
import useFetchContactListData from './useFetchContactListData';
import useFocusListOnReturn from './useFocusListOnReturn';
import { jsx as _jsx } from "react/jsx-runtime";
var ContactList = function ContactList(_ref) {
  var selected = _ref.selected,
    _ref$extensionFilter = _ref.extensionFilter,
    extensionFilter = _ref$extensionFilter === void 0 ? '' : _ref$extensionFilter,
    _ref$showActionButton = _ref.showActionButtons,
    showActionButtons = _ref$showActionButton === void 0 ? true : _ref$showActionButton,
    _ref$disableItemsWith = _ref.disableItemsWithoutNumber,
    disableItemsWithoutNumber = _ref$disableItemsWith === void 0 ? false : _ref$disableItemsWith,
    onItemSelected = _ref.onItemSelected,
    _ref$showOverviewRow = _ref.showOverviewRow,
    showOverviewRow = _ref$showOverviewRow === void 0 ? true : _ref$showOverviewRow,
    _ref$selectionFollows = _ref.selectionFollowsFocus,
    selectionFollowsFocus = _ref$selectionFollows === void 0 ? true : _ref$selectionFollows,
    _ref$refocusOnReturn = _ref.refocusOnReturn,
    refocusOnReturn = _ref$refocusOnReturn === void 0 ? true : _ref$refocusOnReturn;
  var _useAppContext = useAppContext(),
    i18next = _useAppContext.i18next;
  var containerRef = useRef(null);
  var listRef = useRef(null);
  var _useState = useState({}),
    _useState2 = _slicedToArray(_useState, 2),
    sectionOpenStates = _useState2[0],
    setSectionOpenStates = _useState2[1];
  useFetchContactListData();
  var isLoading = useAppSelector(selectExtensionsAreLoading);
  var rows = useContactListRows({
    searchText: extensionFilter,
    showOverviewRow: showOverviewRow,
    sectionOpenStates: sectionOpenStates
  });

  // react-window keys each row element by its key, so focus and the row's
  // own state, such as an open menu, stay with its contact when the list
  // sorts again. The Tab stop and the highlight name their row by it too.
  var rowKeys = useMemo(function () {
    return uniqueRowKeys(rows, contactListRowKey);
  }, [rows]);
  var _useContactListSelect = useContactListSelection({
      rows: rows,
      rowKeys: rowKeys,
      selected: selected,
      searchText: extensionFilter,
      onItemSelected: onItemSelected,
      selectionFollowsFocus: selectionFollowsFocus
    }),
    selectionStore = _useContactListSelect.selectionStore,
    openRowStore = _useContactListSelect.openRowStore,
    findEntryIndex = _useContactListSelect.findEntryIndex,
    onRowClick = _useContactListSelect.onRowClick,
    onRowFocus = _useContactListSelect.onRowFocus,
    onFocusLeave = _useContactListSelect.onFocusLeave;
  var rowToFocus = useCallback(function () {
    var entryIndex = findEntryIndex();
    var entryRowIsFocusable = entryIndex !== -1 && rows[entryIndex] !== undefined && rowIsFocusable(rows[entryIndex]);
    return entryRowIsFocusable ? entryIndex : firstIndexToFocus(rows, rowIsFocusable);
  }, [rows, findEntryIndex]);
  var focusPendingRow = useFocusListOnReturn({
    isEnabled: refocusOnReturn,
    containerRef: containerRef,
    listRef: listRef,
    rowToFocus: rowToFocus
  });
  var _useListTabStop = useListTabStop({
      containerRef: containerRef,
      listRef: listRef,
      rows: rows,
      rowKeys: rowKeys,
      rowIsFocusable: rowIsFocusable,
      entryIndex: rowToFocus,
      onRowFocus: onRowFocus,
      onFocusLeave: onFocusLeave
    }),
    tabStopStore = _useListTabStop.tabStopStore,
    onFocus = _useListTabStop.onFocus,
    onBlur = _useListTabStop.onBlur,
    onKeyDown = _useListTabStop.onKeyDown,
    onItemsRendered = _useListTabStop.onItemsRendered,
    onRowUnmountWithFocus = _useListTabStop.onRowUnmountWithFocus;
  var onRowsRendered = useCallback(function (renderedRows) {
    focusPendingRow();
    onItemsRendered(renderedRows);
  }, [focusPendingRow, onItemsRendered]);
  var onToggleSection = useCallback(function (sectionId, isOpen) {
    saveDepartmentState(sectionId, !isOpen);
    setSectionOpenStates(function (openStates) {
      return _objectSpread(_objectSpread({}, openStates), {}, _defineProperty({}, sectionId, !isOpen));
    });
  }, []);
  var itemData = useMemo(function () {
    return {
      rows: rows,
      searchText: extensionFilter,
      rowKeys: rowKeys,
      selectionStore: selectionStore,
      openRowStore: openRowStore,
      marksOpenPage: selectionFollowsFocus,
      tabStopStore: tabStopStore,
      showActionButtons: showActionButtons,
      disableItemsWithoutNumber: disableItemsWithoutNumber,
      onRowClick: onRowClick,
      onToggleSection: onToggleSection,
      onRowUnmountWithFocus: onRowUnmountWithFocus
    };
  }, [rows, extensionFilter, rowKeys, selectionStore, openRowStore, selectionFollowsFocus, tabStopStore, showActionButtons, disableItemsWithoutNumber, onRowClick, onToggleSection, onRowUnmountWithFocus]);
  if (isLoading) {
    return /*#__PURE__*/_jsx(Loading, {});
  }

  // react-window never clears its row height cache on its own.
  // https://react-window.now.sh/#/api/VariableSizeList
  if (listRef.current) {
    listRef.current.resetAfterIndex(0, false);
  }
  var searchFoundNothing = !isEmptyNullOrUndefined(extensionFilter) && rows.length === 0;
  return /*#__PURE__*/_jsx("div", {
    // NOSONAR
    // Not a control: see useListTabStop.
    ref: containerRef,
    className: "z-20 flex h-full grow flex-col",
    "data-context-menu": "false",
    onFocus: onFocus,
    onBlur: onBlur,
    onKeyDown: onKeyDown,
    children: /*#__PURE__*/_jsx("div", {
      className: "flex h-full flex-1 flex-col",
      children: /*#__PURE__*/_jsx("div", {
        className: "flex flex-1",
        children: searchFoundNothing ? /*#__PURE__*/_jsx(NoMatchOnSearch, {
          reserveScrollbarGutter: true,
          children: i18next.t('Colleagues.SearchEmpty.Description', {
            filterText: extensionFilter
          })
        }) : /*#__PURE__*/_jsx(AutoSizer, {
          className: "left-nav-scroll-wrapper size-full",
          disableWidth: true,
          children: function children(_ref2) {
            var height = _ref2.height;
            return /*#__PURE__*/_jsx(VariableSizeList, {
              ref: listRef,
              overscanCount: 7,
              height: height,
              width: "100%",
              itemData: itemData,
              itemKey: listItemKey,
              itemCount: rows.length,
              itemSize: function itemSize(index) {
                return contactListRowHeight(rows[index]);
              },
              onItemsRendered: onRowsRendered,
              className: "scrollbar left-nav-scroll relative w-full",
              style: {
                overflowY: 'scroll',
                direction: 'inherit'
              },
              children: ContactListRow
            });
          }
        })
      })
    })
  });
};
export default ContactList;